import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PopupComponent } from '../popup/popup.component';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { LeadStatusValues } from '../../../pages/leads/models/lead-status';
import { SearchService } from '../../../pages/search/services/search.service';
import { CommonService } from '../../services/common/common.service';
import { ToastType } from '../../interfaces/common';
import { catchError, of, Observable, takeUntil, tap, throwError } from 'rxjs';

export interface UnassignStatusOption {
  value: string;
  label: string;
  description: string;
  reassuranceNote: string;
  icon: 'pause' | 'minus' | 'info' | 'close';
  colorClass: 'amber' | 'purple' | 'blue' | 'rose';
}

export const GIVEN_UNASSIGN_STATUS_OPTIONS: UnassignStatusOption[] = [
  {
    value: LeadStatusValues['on_hold'], // '4'
    label: 'On hold',
    description: 'Paused for now — you may come back to this.',
    reassuranceNote:
      'You can come back, reassign this lead, and change its status at any time.',
    icon: 'pause',
    colorClass: 'amber',
  },
  {
    value: LeadStatusValues['not_currently_pursuing'], // '6'
    label: 'Not currently pursuing',
    description: 'Not the right fit at this time.',
    reassuranceNote:
      'You can come back, reassign this lead, and change its status at any time.',
    icon: 'minus',
    colorClass: 'purple',
  },
  {
    value: LeadStatusValues['further_information_required'], // '2'
    label: 'Further information required',
    description: 'Waiting to hear back from them.',
    reassuranceNote:
      'You can come back, reassign this lead, and change its status at any time.',
    icon: 'info',
    colorClass: 'blue',
  },
  {
    value: LeadStatusValues['declined'], // '5'
    label: 'Declined',
    description: "This one's closed.",
    reassuranceNote:
      'You can come back, reassign this lead, and change its status at any time.',
    icon: 'close',
    colorClass: 'rose',
  },
];

@Component({
  selector: 'app-unassign-lead-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, PopupComponent],
  templateUrl: './unassign-lead-popup.component.html',
  styleUrl: './unassign-lead-popup.component.scss',
})
export class UnassignLeadPopupComponent
  extends DestroyBase
  implements OnInit, OnChanges
{
  @Input({ required: true }) leadName!: string;
  @Input() leadUniqueId?: string;
  @Input() currentStatus?: string | number | null = null;
  @Input() statusNotes?: string;
  @Input() leadAttributes?: Record<string, unknown> | null;
  @Input() popupPosition: 'fixed' | 'absolute' = 'fixed';
  @Input() isSubmitting = false;

  @Output() confirm = new EventEmitter<{
    selectedStatus?: string;
    statusNotes?: string;
  }>();
  @Output() cancel = new EventEmitter<void>();

  private searchService = inject(SearchService, { optional: true });
  private commonService = inject(CommonService, { optional: true });

  readonly options: UnassignStatusOption[] = GIVEN_UNASSIGN_STATUS_OPTIONS;
  selectedStatus: string | null = null;
  needsStatusSelection = false;

  // Status notes state from lead_attributes
  currentStatusNotes = '';
  originalStatusNotes = '';
  isSavingNote = false;

  ngOnInit(): void {
    this.evaluateStatusRequirement();
    this.syncStatusNotes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentStatus']) {
      this.evaluateStatusRequirement();
    }
    if (changes['statusNotes'] || changes['leadAttributes']) {
      this.syncStatusNotes();
    }
  }

  get popupHeading(): string {
    return this.needsStatusSelection ? "What's the status?" : 'Are you sure?';
  }

  get confirmButtonText(): string {
    if (this.isSubmitting || this.isSavingNote) {
      return 'Unassigning...';
    }
    return 'Unassign';
  }

  get isConfirmDisabled(): boolean {
    if (this.isSubmitting || this.isSavingNote) {
      return true;
    }
    if (this.needsStatusSelection) {
      return !this.selectedStatus;
    }
    return false;
  }

  private syncStatusNotes(): void {
    if (this.statusNotes !== undefined && this.statusNotes !== null) {
      this.currentStatusNotes = String(this.statusNotes);
    } else if (
      this.leadAttributes?.['status_notes'] !== undefined &&
      this.leadAttributes?.['status_notes'] !== null
    ) {
      this.currentStatusNotes = String(this.leadAttributes['status_notes']);
    } else {
      this.currentStatusNotes = '';
    }
    this.originalStatusNotes = this.currentStatusNotes;
  }

  get isNotesDirty(): boolean {
    return (
      (this.currentStatusNotes || '').trim() !==
      (this.originalStatusNotes || '').trim()
    );
  }

  private saveStatusNotesObservable(): Observable<unknown> {
    const trimmed = (this.currentStatusNotes || '').trim();
    if (!this.leadUniqueId || !this.searchService) {
      this.originalStatusNotes = trimmed;
      if (this.leadAttributes) {
        this.leadAttributes['status_notes'] = trimmed;
      }
      return of(null);
    }
    this.isSavingNote = true;

    return this.searchService
      .updateLeadAttributes(this.leadUniqueId, { status_notes: trimmed })
      .pipe(
        tap(() => {
          this.isSavingNote = false;
          this.originalStatusNotes = trimmed;
          if (this.leadAttributes) {
            this.leadAttributes['status_notes'] = trimmed;
          }
        }),
        catchError((err) => {
          this.isSavingNote = false;
          return throwError(() => err);
        }),
      );
  }

  /**
   * Evaluates if this lead requires selecting an unassign reason status.
   * Condition: Lead must already have an active status, and that status
   * must NOT already be one of the 4 given statuses (4, 6, 2, 5).
   */
  private evaluateStatusRequirement(): void {
    if (
      this.currentStatus === null ||
      this.currentStatus === undefined ||
      this.currentStatus === ''
    ) {
      this.needsStatusSelection = false;
      return;
    }

    let raw = '';
    if (typeof this.currentStatus === 'object') {
      const obj = this.currentStatus as Record<string, unknown>;
      raw = String(
        obj?.['value'] ?? obj?.['status'] ?? obj?.['label'] ?? '',
      ).trim();
    } else {
      raw = String(this.currentStatus).trim();
    }
    const normalized = raw.toLowerCase().replace(/_/g, ' ');

    const emptyIndicators = [
      '',
      '0',
      'null',
      'undefined',
      'not defined',
      'select lead status',
      'not set',
    ];
    if (emptyIndicators.includes(normalized)) {
      this.needsStatusSelection = false;
      return;
    }

    const givenStatusValues = ['4', '6', '2', '5'];
    const givenStatusNames = [
      'on hold',
      'not currently pursuing',
      'further information required',
      'further info required',
      'declined',
    ];

    const isAlreadyInGivenStatus =
      givenStatusValues.includes(raw) || givenStatusNames.includes(normalized);

    this.needsStatusSelection = !isAlreadyInGivenStatus;
  }

  get selectedOption(): UnassignStatusOption | undefined {
    return this.options.find((opt) => opt.value === this.selectedStatus);
  }

  get currentStatusLabel(): string {
    if (
      this.currentStatus === null ||
      this.currentStatus === undefined ||
      this.currentStatus === ''
    ) {
      return '';
    }

    let raw = '';
    if (typeof this.currentStatus === 'object') {
      const obj = this.currentStatus as Record<string, unknown>;
      raw = String(
        obj?.['label'] ??
          obj?.['name'] ??
          obj?.['value'] ??
          obj?.['status'] ??
          '',
      ).trim();
    } else {
      raw = String(this.currentStatus).trim();
    }

    if (!raw) return '';

    const lower = raw.toLowerCase();
    const normalized = lower.replace(/_/g, ' ');

    const emptyIndicators = [
      '0',
      'null',
      'undefined',
      'not defined',
      'select lead status',
      'not set',
    ];
    if (emptyIndicators.includes(normalized)) {
      return '';
    }

    const matchedOption = this.options.find(
      (o) =>
        o.value === raw ||
        o.label.toLowerCase() === lower ||
        o.label.toLowerCase() === normalized,
    );
    if (matchedOption) return matchedOption.label;

    const statusMap: Record<string, string> = {
      '1': 'Under Review',
      '2': 'Further Information Required',
      '3': 'Prospective Client',
      '4': 'On Hold',
      '5': 'Declined',
      '6': 'Not Currently Pursuing',
      '7': 'Active Client',
      '8': 'Request Lead Contact',
      '9': 'Active Funded',
      '10': 'Active Funded Grant',
      '11': 'Active Technical Assistance',
      under_review: 'Under Review',
      'under review': 'Under Review',
      further_information_required: 'Further Information Required',
      'further information required': 'Further Information Required',
      'further info required': 'Further Information Required',
      prospective_client: 'Prospective Client',
      'prospective client': 'Prospective Client',
      on_hold: 'On Hold',
      'on hold': 'On Hold',
      declined: 'Declined',
      not_currently_pursuing: 'Not Currently Pursuing',
      'not currently pursuing': 'Not Currently Pursuing',
      active_client: 'Active Client',
      'active client': 'Active Client',
      request_lead_contact: 'Request Lead Contact',
      'request lead contact': 'Request Lead Contact',
      active_funded: 'Active Funded',
      'active funded': 'Active Funded',
      active_funded_grant: 'Active Funded Grant',
      'active funded grant': 'Active Funded Grant',
      active_technical_assistance: 'Active Technical Assistance',
      'active technical assistance': 'Active Technical Assistance',
      active_ta: 'Active Technical Assistance',
      'active ta': 'Active Technical Assistance',
    };

    if (statusMap[raw]) return statusMap[raw];
    if (statusMap[lower]) return statusMap[lower];
    if (statusMap[normalized]) return statusMap[normalized];

    return normalized
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  selectOption(option: UnassignStatusOption): void {
    if (this.isSubmitting || this.isSavingNote) return;
    this.selectedStatus = option.value;
  }

  onContinue(): void {
    if (this.isSubmitting || this.isSavingNote) return;

    if (this.needsStatusSelection && !this.selectedStatus) {
      return;
    }

    const payload: { selectedStatus?: string; statusNotes?: string } = {
      ...(this.needsStatusSelection && this.selectedStatus
        ? { selectedStatus: this.selectedStatus }
        : {}),
      statusNotes: this.currentStatusNotes.trim(),
    };

    if (this.isNotesDirty) {
      this.isSavingNote = true;
      this.saveStatusNotesObservable()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isSavingNote = false;
            this.confirm.emit(payload);
          },
          error: (err) => {
            console.error('Error saving status notes before unassigning:', err);
            this.isSavingNote = false;
            this.commonService?.triggerToast({
              type: ToastType.Error,
              message: 'Failed to save status notes. Please try again.',
            });
          },
        });
      return;
    }

    this.confirm.emit(payload);
  }

  onCancel(): void {
    if (this.isSubmitting || this.isSavingNote) return;
    this.cancel.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeHandler(event: KeyboardEvent): void {
    if (!this.isSubmitting && !this.isSavingNote) {
      event.preventDefault();
      this.onCancel();
    }
  }
}
